import React, { useEffect } from "react";
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
} from "@mui/lab";
import { Box, Typography, Chip, Avatar } from "@mui/material";
import dayjs from "dayjs";
import { safe_get } from "../../report-visuals";

const getStatusColor = (
    status?: string
): "success" | "primary" | "warning" | "error" | "info" | "grey" => {
    if (!status) return "grey";

    const s = status.toLowerCase();

    if (s.includes("create")) return "info";
    if (s.includes("update")) return "warning";
    if (s.includes("assign")) return "primary";
    if (s.includes("close") || s.includes("complete")) return "success";
    if (s.includes("delete") || s.includes("reject")) return "error";

    return "grey"; // ✅ SAFE FALLBACK
};

export const TimelineLog = ({ timeline_log = [] }: any) => {
    useEffect(() => {
        console.log("🕒 Timeline Log FULL DATA:", timeline_log);
    }, [timeline_log]);

    if (!Array.isArray(timeline_log) || timeline_log.length === 0) return null;

    return (
        <Timeline
            position="right"
            sx={{
                p: 0,
                mt: 1,
                "& .MuiTimelineItem-root:before": {
                    flex: 0,
                    padding: 0,
                },
            }}
        >
            {[...timeline_log].reverse().map((x, i) => {
                const userName = safe_get(x, "who.name", "User");
                const status = safe_get(x, "status", "");
                const comments = safe_get(x, "comments", "");
                const additional = safe_get(x, "additional", "");
                const time = x?.ts
                    ? dayjs(x.ts).format("MMM DD, YYYY • hh:mm A")
                    : "";

                const color = getStatusColor(status);

                return (
                    <TimelineItem key={i}>
                        <TimelineSeparator>
                            <TimelineDot
                                color={color}
                                sx={{ boxShadow: "none" }}
                            />
                            {i !== timeline_log.length - 1 && (
                                <TimelineConnector
                                    sx={{ bgcolor: "#E5E7EB" }}
                                />
                            )}
                        </TimelineSeparator>

                        <TimelineContent sx={{ pb: 3 }}>
                            <Box
                                sx={{
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "12px",
                                    border: "1px solid #E5E7EB",
                                    padding: "16px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                }}
                            >
                                {/* Header */}
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Avatar
                                        src={safe_get(
                                            x,
                                            "who.profileImage",
                                            "/images/user.png"
                                        )}
                                        sx={{ width: 36, height: 36 }}
                                    />

                                    <Box ml={2} flex={1}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: "#4F46E5",
                                            }}
                                        >
                                            {userName}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: "#6B7280" }}
                                        >
                                            {time}
                                        </Typography>
                                    </Box>

                                    {status && (
                                        <Chip
                                            label={status}
                                            size="small"
                                            // @ts-ignore
                                            color={color}
                                            sx={{
                                                textTransform: "capitalize",
                                                fontWeight: 500,
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Body */}
                                {comments && (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#374151", mt: 1 }}
                                    >
                                        {comments}
                                    </Typography>
                                )}

                                {additional && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1,
                                            fontStyle: "italic",
                                            color: "#374151",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {typeof additional === "string"
                                            ? additional
                                            : JSON.stringify(
                                                  additional,
                                                  null,
                                                  2
                                              )}
                                    </Typography>
                                )}
                            </Box>
                        </TimelineContent>
                    </TimelineItem>
                );
            })}
        </Timeline>
    );
};
